/**
 * The standfirst under each section heading. **The designs caption every section
 * and there is no field behind any of them**, so each one here is ours — and the
 * test is whether it is true of *every* record. "Definition and scope" describes
 * any Description section; the design's "Direct contextual observation of users
 * in action" describes Field Study alone, so the method-specific ones are
 * dropped rather than guessed at.
 *
 * **Keyed by field rather than by page, and so are the headings.** The designs
 * put "What" over `descriptionText` on a phase and "Description" over the same
 * field on a process, which would make "What" name two things one click apart.
 * Confluence's own heading wins, because it is what the scheduler matches on.
 */

/** A section's heading and the line under it, for one content field. */
export type SectionCopy = { heading: string; caption?: string };

export const SECTION_COPY = {
  description: {
    heading: 'Description',
    caption: 'Definition and scope',
  },
  why: {
    heading: 'Why',
    caption: 'Value rationale and risk mitigation',
  },
  what: {
    heading: 'What',
    caption: 'Core artifacts, models, and evidence',
  },
  how: {
    heading: 'How',
    caption: 'Execution framework and delivery methods',
  },
  /** L3 only: a method answers "how" as a numbered path rather than as prose. */
  howSteps: {
    heading: 'How (Step-by-Step)',
    caption: 'The chronological execution path',
  },
  /** L3 only: a method answers "what" as a list of outputs. */
  whatYouDeliver: {
    heading: 'What You Deliver',
    caption: 'Core output artifacts',
  },
  processes: {
    heading: 'Processes',
    caption: 'Taxonomy of methods',
  },
  methods: {
    heading: 'Methods',
    caption: 'Method library sitting underneath this process',
  },
  masteryLevels: {
    heading: 'Mastery Levels',
    caption: 'Progression scale and target certification',
  },
  conditions: {
    heading: 'Pre & Post Conditions',
    caption: 'Stage boundaries',
  },
  aiAugmentation: {
    // The design's caption here is about Field Study, not about the section.
    heading: 'AI Augmentation',
  },
  comingSoon: {
    heading: 'Coming Soon',
  },
} as const satisfies Record<string, SectionCopy>;

/**
 * The four ICOM cards, in the order the designs lay them out.
 *
 * **Column-major, which is why this is an ordered list.** Reading down each
 * column gives "what goes in / what constrains it" beside "what comes out / who
 * does it"; a row-major grid would pair Inputs with Outputs, which is a
 * different and wrong reading.
 *
 * The titles are verbatim from Confluence — `+`, not `&` — because that is what
 * the scheduler matches on and what a reader following the link will see.
 */
export const ICOM_CARDS = [
  {
    field: 'inputsAndPreConditionsList',
    heading: 'Inputs + Pre-conditions',
    caption: 'What is required to begin',
    column: 'left',
  },
  {
    field: 'controlsAndSupportList',
    heading: 'Controls + Support',
    caption: 'Compliance, policies, and frameworks',
    column: 'left',
  },
  {
    field: 'outputsAndPostConditionsList',
    heading: 'Outputs + Post-conditions',
    caption: 'What is generated upon completion',
    column: 'right',
  },
  {
    field: 'rolesAndResourcesList',
    heading: 'Roles + Resources',
    caption: 'Key participants and tooling resources',
    column: 'right',
  },
] as const;

export type IcomField = (typeof ICOM_CARDS)[number]['field'];
